import React from "react";
import { FiCalendar, FiUser } from "react-icons/fi";
import { Container, Row, Col, Card, Form, Alert } from "react-bootstrap";
import Select from "react-select";
import LocationMap from "./LocationMap";
import LocationTable from "./LocationTable";
import LiveLocationAPI from "../../api/LiveLocationAPI";
import Util from "../../util/Util";
import withRouter from "../../util/WithRouter";

class LocationTracker extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDate: "",
      selectedSalesPerson: null, 
      salesPersons: [],
      locations: [],
      isLoading: false,
    };
  }

  componentDidMount() {
    this.loadSalesPersons();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.selectedSalesPerson !== this.state.selectedSalesPerson ||
      prevState.selectedDate !== this.state.selectedDate
    ) {
      this.fetchLocations();
    }
  }

  loadSalesPersons() {
    try {
      const model = JSON.parse(localStorage.getItem("model") || "{}");
      const users = model.user?.options || [];
      
      const liveUsers = users.filter(u => u.is_live_tracking === 1);
      const userList = liveUsers.map(u => ({
        value: u.id || u.user_id,
        label: u.name || u.label
      }));

      this.setState({ salesPersons: userList });
    } catch (e) {
      console.error("Error parsing user model from localStorage", e);
    }
  }

  fetchLocations() {
    const { selectedSalesPerson, selectedDate } = this.state;

    if (!selectedSalesPerson || !selectedDate) {
      this.setState({ locations: [] });
      return;
    }

    this.setState({ isLoading: true });

    const dateObj = new Date(selectedDate);
    dateObj.setHours(23, 59, 59, 0);
    const formattedDate = Util.dateToServerDateTime(dateObj);

    const payload = {
      user_id: selectedSalesPerson.value,
      date_time: formattedDate,
    };

    LiveLocationAPI.list(
      payload,
      (res) => {
        const data = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.body)
          ? res.body
          : Array.isArray(res)
          ? res
          : [];

        if (data.length > 0) {
          const mappedData = data.map((item, index) => {
            const dt = new Date(item.date_time);
            return {
              id: item.id || `loc-${index}`,
              date: dt.toISOString().split("T")[0],
              time: dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              address: item.address || "",
              battery: item.battery_level || 0,
              salesPerson: item.user_name || "User",
              lat: parseFloat(item.latitude || 0),
              lng: parseFloat(item.longitude || 0),
              timestamp: dt.getTime()
            };
          });
          
          mappedData.sort((a, b) => a.timestamp - b.timestamp);
          this.setState({ locations: mappedData, isLoading: false });
        } else {
          this.setState({ locations: [], isLoading: false });
        }
      },
      (err) => {
        console.error("Error fetching live locations", err);
        this.setState({ locations: [], isLoading: false });
      }
    );
  }

  handleDateChange = (e) => this.setState({ selectedDate: e.target.value });
  handleSalesPersonChange = (option) => this.setState({ selectedSalesPerson: option });

  render() {
    const { selectedDate, selectedSalesPerson, salesPersons, locations, isLoading } = this.state;

    return (
      <Container fluid className="py-4">
        {/* Header */}
        <div className="mb-4">
          <h2 className="fw-bold">Live Location Tracking</h2>
          <p className="text-muted mb-0">Real-time monitoring of sales team locations</p>
        </div>

        {/* Filters (Width restricted to prevent full-screen spanning) */}
        <Row className="mb-4 g-3">
          {/* Date Picker */}
          <Col md={3}>
            <Card className="p-3 border-0 shadow-sm h-100">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex justify-content-center align-items-center" style={{ width: 40, height: 40 }}>
                  <FiCalendar size={20} className="text-primary"/>
                </div>
                <div className="flex-grow-1">
                  <Form.Group controlId="filterDate">
                    <Form.Label className="fw-semibold small text-muted mb-1">Select Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={selectedDate}
                      onChange={this.handleDateChange}
                      className="shadow-none"
                      aria-label="Select Date"
                    />
                  </Form.Group>
                </div>
              </div>
            </Card>
          </Col>

          {/* Sales Person Selector */}
          <Col md={4}>
            <Card className="p-3 border-0 shadow-sm h-100">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex justify-content-center align-items-center" style={{ width: 40, height: 40 }}>
                  <FiUser size={20} className="text-success"/>
                </div>
                <div className="flex-grow-1">
                  <Form.Group controlId="filterSalesPerson">
                    <Form.Label htmlFor="filterSalesPerson" className="fw-semibold small text-muted mb-1">
                      Sales Person
                    </Form.Label>
                    <Select
                      inputId="filterSalesPerson"
                      options={salesPersons}
                      value={selectedSalesPerson}
                      onChange={this.handleSalesPersonChange}
                      placeholder="Select Sales Person"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({ ...base, minHeight: '38px', boxShadow: 'none' }),
                      }}
                    />
                  </Form.Group>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Alerts / Notices */}
        {!selectedDate || !selectedSalesPerson ? (
          <Alert variant="warning" role="alert">
            Please select both <b>Date</b> and <b>Sales Person</b> to view data.
          </Alert>
        ) : isLoading ? (
          <Alert variant="info" role="status">
            <div className="d-flex align-items-center gap-2">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Fetching location data...
            </div>
          </Alert>
        ) : locations.length === 0 ? (
          <Alert variant="info" role="alert">No location data available for selected filters.</Alert>
        ) : null}

        {/* Main Content */}
        {selectedDate && selectedSalesPerson && locations.length > 0 && !isLoading && (
          <Row>
            <Col lg={6} className="mb-4">
              <LocationTable data={locations} />
            </Col>
            <Col lg={6}>
              <LocationMap data={locations} />
            </Col>
          </Row>
        )}
      </Container>
    );
  }
}

export default withRouter(LocationTracker);